import React, { useCallback, useEffect, useMemo, useState } from "react";
import "./index.scss";
import { CategoryType, EnhancedPoi, EnhancedPoiCoupon, Operation, Sku } from "@/types/poiCard";
import { useJumpToPage } from "@/hooks/useJumpToPage";
import useCountdown from "@/hooks/useCountdown";
import { PoiCardClickPosition, PoiCouponBuType, PoiCouponRecallStatus, ProductTagStyleClassMap } from "./constants";
import { useTracker } from "@/context/TrackerContext";
import Exposure from "@/components/exposure";
import { SupplyCallbackType, SupplyListLxConfig } from "@/types/supplyList";
import { notNull, notZero } from "@/lib/utils";
import { COUPON_SUPPLY_BID_MC, COUPON_SUPPLY_BID_MV } from "@/track/coupon-wall";
import { getCouponThresholdRange } from "./utils";
import { reportCustomMetric } from "@/lib/owl";

interface PoiCardCouponItemProps {
  coupon: EnhancedPoiCoupon;
  poiId: string;
  enableCountdown?: boolean; // 是否开启倒计时，倒计时结束不展示下挂券，默认为 false
  lxConfig: SupplyListLxConfig;
  isCouponPlatformStrategyRecall?: boolean; // 是否开启券平台召回最优券策略
  onCouponHide?: () => void; // 倒计时结束回调
  onCouponClick?: () => void; // 下挂券点击回调
}

/**
 * 下挂券
 * @param props 
 * @returns 
 */
const PoiCardCouponItem: React.FC<PoiCardCouponItemProps> = (props) => {
  const {
    coupon = {} as EnhancedPoiCoupon,
    poiId,
    enableCountdown = false,
    lxConfig,
    isCouponPlatformStrategyRecall,
    onCouponHide,
    onCouponClick,
  } = props || {};

  const tracker = useTracker();
  const couponCountdown = useMemo(
    () => enableCountdown
      ? Math.floor((coupon.couponEndTime - Date.now()) / 1000)
      : 0,
    [enableCountdown, coupon.couponEndTime]
  );
  const { countdownText, isExpired } = useCountdown(couponCountdown);

  const lxCustom = useMemo(
    () => ({
      coupon_id: coupon.couponViewId || "",
      wm_item_type: "wm_coupon",
      poi_id: poiId || "",
    }),
    [coupon, poiId]
  );

  const couponSupplyLxCustom = useMemo(() => ({
    user_coupon_id: coupon.couponViewId || "", // 券实例 ID
    coupon_threshold: getCouponThresholdRange(coupon.priceLimit || ""), // 券门槛范围
    discount_coupon_config_id: coupon.couponConfigId || "", // 券配置 ID
    act_sys_src: coupon.actSysSrc || "", // 营销系统来源
  }), []);

  // 下挂券曝光埋点上报
  const handleCouponExpose = useCallback(() => {
    if (tracker) {
      if (lxConfig?.POI_CARD_BID_MV?.COUPON) {
        tracker.moduleView(lxConfig.POI_CARD_BID_MV.COUPON, lxCustom);
      }
      if (isCouponPlatformStrategyRecall) {
        tracker.moduleView(COUPON_SUPPLY_BID_MV.COUPON, couponSupplyLxCustom);
      }
    }
  }, [tracker, lxConfig, lxCustom, isCouponPlatformStrategyRecall, couponSupplyLxCustom]);

  // 下挂券点击埋点上报
  const handleCouponClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (tracker) {
      if (lxConfig?.POI_CARD_BID_MC?.COUPON) {
        tracker.moduleClick(lxConfig.POI_CARD_BID_MC.COUPON, lxCustom);
      }
      if (isCouponPlatformStrategyRecall) {
        tracker.moduleClick(COUPON_SUPPLY_BID_MC.COUPON, couponSupplyLxCustom);
      }
    }
    onCouponClick?.();
  }, [tracker, lxConfig, lxCustom, isCouponPlatformStrategyRecall, couponSupplyLxCustom, onCouponClick])

  useEffect(() => {
    if (enableCountdown && isExpired) {
      onCouponHide?.();
    }
  }, [enableCountdown, isExpired, onCouponHide])

  // 启用券倒计时，当券已过期，不展示下挂券
  if (enableCountdown && isExpired) return null;
  return (
    <Exposure onExpose={handleCouponExpose}>
      <div className="coupon-item" onClick={handleCouponClick}>
        <div className="coupon-amount">
          <span className="amount-symbol">¥</span>
          <span className="amount">{coupon.couponValue}</span>
        </div>
        <div className="coupon-limit">{coupon.priceLimit === '0' ? '无门槛' : `满${coupon.priceLimit}可用`}</div>
        <div className={`go-use ${enableCountdown ? 'has-countdown' : ''}`}>
          <span>去使用</span>
          <img
            className="go-use-arrow"
            src={"https://p0.meituan.net/pepperfe/46c4cb2078fee3998b066e64460b5cc1267.png"}
          />
        </div>
        {enableCountdown && <div className="coupon-countdown">{countdownText}</div>}
      </div>
    </Exposure>
  );
};

const PoiCardSkuItem: React.FC<{
  item: Sku;
  poiId: string;
  index: number;
  lxConfig: SupplyListLxConfig;
  onSkuClick: (e: React.MouseEvent<HTMLDivElement>) => void;
}> = (props) => {
  const { item, poiId, index, lxConfig, onSkuClick } = props || {};
  const tracker = useTracker();

  const lxCustom = useMemo(
    () => ({
      sku_id: item.productId || "",
      wm_item_type: "wm_sku",
      poi_id: poiId || "",
      sku_index: index,
    }),
    [item, poiId, index]
  );

  const handleSkuExpose = useCallback(() => {
    if (tracker && lxConfig?.POI_CARD_BID_MV?.SKU) {
      tracker.moduleView(lxConfig.POI_CARD_BID_MV.SKU, lxCustom);
    }
  }, [tracker, lxConfig, lxCustom]);

  const handleSkuClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation();
      if (tracker && lxConfig?.POI_CARD_BID_MC?.SKU) {
        tracker.moduleClick(lxConfig.POI_CARD_BID_MC.SKU, lxCustom);
      }
      onSkuClick(e);
    },
    [tracker, lxConfig, lxCustom, onSkuClick]
  );

  return (
    <Exposure onExpose={handleSkuExpose}>
      <div className="dish-item" onClick={handleSkuClick}>
        {/* 菜品图片 */}
        <div className="dish-image-container">
          <img className="dish-image" src={item.headImage} />
          {/* 菜品标签 */}
          {notZero(item.savingAmount) && notNull(item.savingAmount) && (
            <div className="dish-tag">
              约省<span className="saving-amount">{item.savingAmount}</span>元
            </div>
          )}
        </div>
        {/* 菜品名称 */}
        <div className="dish-name">{item.title}</div>
        {/* 菜品价格 */}
        <div className="dish-price">
          <span className="price-symbol">¥</span>
          {/* 没有到手价展示原价 */}
          <span>{item.finalPrice || item.originPrice}</span>
          {item.finalPrice && item.originPrice && <div className="origin-price">¥{item.originPrice}</div>}
        </div>
      </div>
    </Exposure>
  );
};

interface PoiCardProps {
  poiInfo: EnhancedPoi;
  index: number;
  tabId?: number | null;
  supplyCallbackType?: SupplyCallbackType;
  enableCountdown?: boolean; // 是否开启倒计时，倒计时结束不展示下挂券，默认为 false
  lxConfig: SupplyListLxConfig;
  isCouponPlatformStrategyRecall?: boolean; // 是否开启券平台召回最优券策略
}


const PoiCard: React.FC<PoiCardProps> = (props) => {
  const { 
    poiInfo: info,
    index,
    tabId,
    supplyCallbackType = SupplyCallbackType.Default,
    enableCountdown,
    lxConfig,
    isCouponPlatformStrategyRecall,
  } = props || {};
  // 用于下挂菜列表区域判断是否展示无券样式
  const [isCouponVisible, setIsCouponVisible] = useState(!!info.coupon);
  const tracker = useTracker();
  const jumpToPage = useJumpToPage();


  // 灵犀参数
  const lxCustom = useMemo(
    () => ({
      poi_id: info?.poiIdStr || "",
      wm_item_type: "wm_poi",
      poi_index: index,
      poi_name: info?.title,
      poi_type: supplyCallbackType,
      tab_id: tabId || "",
    }),
    [info, index, tabId, supplyCallbackType]
  );

  // 跳转商详页
  const jumpToPoiPage = useCallback((operation: Operation) => {
    // 跳转链接为空时，不跳转
    const { jumpLink, jumpLinkType } = operation || {};
    if (!jumpLink || !jumpLinkType) {
      return;
    }
    // sessionId 未绑定时展示 Loading，绑定后自动跳转
    jumpToPage({
      jumpLink,
      jumpType: jumpLinkType,
      source: 'poiCard',
    });
  }, [jumpToPage]);

  // 开启了券平台召回最优券策略，商卡点击时上报埋点
  const reportCouponRecallPoiCardClick = useCallback((clickPosition: PoiCardClickPosition) => {
    if (tracker && isCouponPlatformStrategyRecall) {
      // 商卡点击
      tracker.moduleClick(COUPON_SUPPLY_BID_MC.POI, {
        click_position: clickPosition,
        bu_type: PoiCouponBuType.WAIMAI,
        coupon_recall_status: info.coupon ? PoiCouponRecallStatus.HAS_COUPON : PoiCouponRecallStatus.NO_COUPON,
      });
      // 供给列表点击
      tracker.moduleClick(COUPON_SUPPLY_BID_MC.SUPPLY_LIST);
    }
  }, [tracker, isCouponPlatformStrategyRecall, info]);

  // 点击商卡
  const handlePoiClick = useCallback(() => {
    // 埋点上报
    if (tracker) {
      if (lxConfig?.POI_CARD_BID_MC?.CARD) {
        tracker.moduleClick(lxConfig.POI_CARD_BID_MC.CARD, lxCustom);
      }
      // 开启了券平台召回最优券策略，上报商卡点击
      reportCouponRecallPoiCardClick(PoiCardClickPosition.POI);
    }

    jumpToPoiPage(info?.operation);
  }, [info, lxConfig, lxCustom, jumpToPoiPage, reportCouponRecallPoiCardClick]);

  // 商卡曝光
  const handlePoiExpose = useCallback(() => {
    // 上报商卡曝光次数，页面生命周期内只上报一个
    if (!window.isPoiCardExposeCountReport) {
      window.isPoiCardExposeCountReport = true;
      reportCustomMetric({
        key: "PoiCardExposeCount",
        value: 1,
      })
    }
    if (tracker) {
      if (lxConfig?.POI_CARD_BID_MV?.CARD) {
        tracker.moduleView(lxConfig.POI_CARD_BID_MV.CARD, lxCustom);
      }
      if (isCouponPlatformStrategyRecall) {
        // 开启了券平台召回最优券策略，上报商卡曝光
        tracker.moduleView(COUPON_SUPPLY_BID_MV.POI, {
          bu_type: PoiCouponBuType.WAIMAI,
          coupon_recall_status: info.coupon ? PoiCouponRecallStatus.HAS_COUPON : PoiCouponRecallStatus.NO_COUPON,
        });
      }
    }
  }, [tracker, lxConfig, lxCustom, isCouponPlatformStrategyRecall]);

  // 商卡下挂菜点击
  const handleSkuClick = useCallback((item: Sku) => {
    // 开启了券平台召回最优券策略，上报商卡点击
    reportCouponRecallPoiCardClick(PoiCardClickPosition.SKU);
    jumpToPoiPage(item?.operation);
  }, [reportCouponRecallPoiCardClick, jumpToPoiPage]);

  // 商卡下挂券点击
  const handleCouponClick = useCallback(() => {
    // 开启了券平台召回最优券策略，上报商卡点击
    reportCouponRecallPoiCardClick(PoiCardClickPosition.COUPON);
    jumpToPoiPage(info?.operation);
  }, [reportCouponRecallPoiCardClick, jumpToPoiPage]);

  // 商卡下挂券倒计时结束隐藏
  const handleCouponHide = useCallback(() => {
    setIsCouponVisible(false);
  }, []);

  useEffect(() => {
    setIsCouponVisible(!!info.coupon);
  }, [info])

  return (
    <Exposure onExpose={handlePoiExpose}>
      <div className="poi-card-container" onClick={handlePoiClick}>
        {/* 顶部区域 */}
        <div className="poi-card-top">
          {/* 左侧-商家图片 */}
          <img
            className="poi-card-top-logo"
            src={info.headImage}
            // onClick={handlePoiHeadImageClick}
          />
          <div className="poi-card-top-info">
            {/* 第 1 行 ： 供给类型标签 + 商家名称 + 配送标签 */}
            <div className="poi-name-line">
              {/* 供给类型标签：外卖 */}
              {info?.categoryLabel?.label && (
                <span
                  className={`category-tag ${
                    ProductTagStyleClassMap[info.categoryLabel?.type as CategoryType]
                  }`}
                >
                  {info.categoryLabel?.label}
                </span>
              )}
              {/* 商家名称 */}
              <div className="poi-name">{info.title}</div>
            </div>
            {/* 第 2 行：评分 + 月售 + 起送 + 配送费 + 配送时间 */}
            <div className="shipping-info-line">
              {notZero(info.score) && notNull(info.score) && (
                <div className="shipping-info-star">
                  <img
                    className="star-icon"
                    src="https://p0.meituan.net/pepperfe/c8a6e8b510e65fd93b25078876d69205588.png"
                  />
                  <span className="rating">{info.score}</span>
                </div>
              )}
              {notNull(info.monthSaleNum) && notZero(info.monthSaleNum) && <span>月售{info.monthSaleNum}</span>}
              {notNull(info.minPrice) && <span>起送¥{info.minPrice}</span>}
              {notNull(info.shippingFee) && <span>配送约¥{info.shippingFee}</span>}
              {notZero(info.avgDeliveryTime) && notNull(info.avgDeliveryTime) && (
                <span className="shipping-info-delivery-time">{info.avgDeliveryTime}分钟</span>
              )}
            </div>
            {/* 第 3 行：神券 > 商家满减券 > 榜单标签 > 点评标签 只展示前两个标签 */}
            {(info.magicCouponLabel ||
              (info.cutLabels && info.cutLabels.length > 0) ||
              info.rankLabel ||
              info.dianpingLabel ||
              info.distance) && (
              <div className="label-line">
                <div className="label-wrapper">
                  {/* 神券 */}
                  {info.magicCouponLabel && (
                    <div className="god-coupon">
                      <div className="god-coupon-left">
                        <div className="god-coupon-left-inner">
                          <img
                            className="god-coupon-logo"
                            src="https://p1.meituan.net/pepperfe/1018e92b727fac763b29741baf13a10a3876.png"
                          />
                        </div>
                      </div>
                      <div className="god-coupon-right">
                        <div className="god-coupon-right-inner">
                          <span className="god-coupon-text">{info.magicCouponLabel}</span>
                          <img
                            className="god-coupon-arrow"
                            src="https://s3plus.meituan.net/pepper-fe/huisheng-img/poi-god-coupon-arrow.png"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  {/* 满减券 */}
                  {info.cutLabels && info.cutLabels.length > 0 && (
                    <div className="discount-coupons">
                      {info.cutLabels?.map((item, index) => (
                        <React.Fragment key={index}>
                          <div className="discount-text">{item}</div>
                          {index < info.cutLabels.length - 1 && (
                            <div className="discount-divider" />
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  )}
                  {/* 榜单标签 */}
                  {info.rankLabel && (
                    <div className="tag">
                      <span>{info.rankLabel}</span>
                    </div>
                  )}
                  {/* 点评标签 */}
                  {info.dianpingLabel && (
                    <div className="tag">
                      <span>{info.dianpingLabel}</span>
                    </div>
                  )}
                </div>
                {/* 配送距离 */}
                {notNull(info.distance) && notZero(info.distance) && (
                  <span className="shipping-info-distance" style={{ marginRight: 0 }}>
                    {info.distance}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
        {/* 底部菜品区域 */}
        <div className="poi-card-bottom">
          {/* 下挂券 */}
          {info.coupon && (
            <PoiCardCouponItem 
              coupon={info.coupon!}
              poiId={info.poiIdStr}
              enableCountdown={enableCountdown}
              lxConfig={lxConfig}
              isCouponPlatformStrategyRecall={isCouponPlatformStrategyRecall}
              onCouponHide={handleCouponHide}
              onCouponClick={handleCouponClick}
            />
          )}
          {/* 下挂菜 */}
           {/* 没有券展示时，增加 padding-left 留出券位置 */}
          <div className={`poi-sku-list-wrapper ${isCouponVisible ? '' : 'no-coupon'}`}>
            {info.skuList &&
              info.skuList.length > 0 &&
              info.skuList?.map((item, index) => (
                <PoiCardSkuItem
                  key={`${item.productId}-${index}`}
                  item={item}
                  poiId={info.poiIdStr}
                  index={index + 1}
                  lxConfig={lxConfig}
                  onSkuClick={() => handleSkuClick(item)}
                />
              ))}
          </div>
        </div>
      </div>
    </Exposure>
  );
};

/**
 * Poi 卡片的骨架屏
 */
export const PoiCardSkeleton = () => {
  return (
    <div className="poi-card-container-skeleton">
      {/* 顶部区域 */}
      <div className="poi-card-top">
        <div className="poi-card-top-logo skeleton-item"></div>
        <div className="poi-card-top-info">
          <div className="poi-card-top-line skeleton-item"></div>
          <div className="poi-card-top-line skeleton-item"></div>
          <div className="poi-card-top-line skeleton-item"></div>
        </div>
      </div>
      {/* 底部菜品区域 */}
      <div className="poi-card-bottom">
        <div className="dish-item skeleton-item"></div>
        <div className="dish-item skeleton-item"></div>
        <div className="dish-item skeleton-item"></div>
        <div className="dish-item skeleton-item"></div>
      </div>
    </div>
  );
};

export default PoiCard;
